import {
  CalendarMonth as CalendarIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  SwapHoriz as TransferIcon,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  BackOrderReportParams,
  BackOrderReportRecord,
} from "../../../@types/DRO/AllReportsResponse";
import { GetAllWarehouseApiResponse } from "../../../@types/DRO/GetAllWarehouseResponse";
import { AllReportsApi } from "../../../api/AllReportsDownload/AllReportsApi";
import { GetAllWarehouseListApi } from "../../../api/GetWarehouseList/GetAllWarehouseListApi";

const BackOrderReport: React.FC = () => {
  // State for data and pagination
  const [data, setData] = useState<BackOrderReportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

   const getDefaultDates = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  // State for filters
  const [filters, setFilters] = useState<BackOrderReportParams>({
    source_location: undefined,
    destination_location: undefined,
    part_code: undefined,
    part_name: "",
    to_no: undefined,
   start_date: getDefaultDates().startDate,
    end_date: getDefaultDates().endDate,
    page: 1,
    page_size: 50,
  });

  // State for dropdowns
  const [warehouses, setWarehouses] = useState<
    GetAllWarehouseApiResponse["data"]["data"]
  >([]);

  // Selected values
  const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState<
    GetAllWarehouseApiResponse["data"]["data"][0] | null
  >(null);
  const [selectedDestinationWarehouse, setSelectedDestinationWarehouse] =
    useState<GetAllWarehouseApiResponse["data"]["data"][0] | null>(null);

  // Pagination for dropdowns
  const [warehousesCurrentPage, setWarehousesCurrentPage] = useState(0);
  const [warehousesSearchQuery, setWarehousesSearchQuery] = useState("");
  const [hasMoreWarehouses, setHasMoreWarehouses] = useState(true);
  const rowsPerPage = 10;

  const handleNumericInputChange =
    (field: "part_code" | "to_no" | "reference_code") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      // Allow only numbers and empty string
      if (value === "" || /^[0-9]+$/.test(value)) {
        setFilters({
          ...filters,
          [field]: value === "" ? undefined : parseInt(value, 10),
        });
        setCurrentPage(1);
      }
    };
  // Fetch warehouses
  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await GetAllWarehouseListApi.api.getAllWarehouseList(
        warehousesCurrentPage,
        rowsPerPage,
        warehousesSearchQuery
      );
      setWarehouses((prev) =>
        warehousesCurrentPage === 0
          ? response.data.data
          : [...prev, ...response.data.data]
      );
      setHasMoreWarehouses(response.data.data.length === rowsPerPage);
    } catch (error) {
      console.error("Error fetching warehouses", error);
    }
  }, [warehousesCurrentPage, warehousesSearchQuery]);

  // Handle scroll for infinite loading
  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;

    if (listboxNode.scrollHeight - position <= 1) {
      if (hasMoreWarehouses) {
        setWarehousesCurrentPage((prev) => prev + 1);
      }
    }
  };

  // Fetch data function
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: BackOrderReportParams = {
        ...filters,
        source_location: selectedSourceWarehouse?.id,
        destination_location: selectedDestinationWarehouse?.id,
        page: currentPage,
        page_size: pageSize,
      };

      // Remove empty string values
      Object.keys(params).forEach((key) => {
        const k = key as keyof BackOrderReportParams;
        if (params[k] === "" || params[k] === undefined) {
          delete params[k];
        }
      });

      const response = await AllReportsApi.api.getBackOrderReport(params);
      if (response.success) {
        setData(response.records);
        setTotalPages(response.total_pages);
      }
    } catch (err) {
      setError("Failed to fetch back order report. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    currentPage,
    pageSize,
    selectedSourceWarehouse,
    selectedDestinationWarehouse,
  ]);

  // Initial fetch for dropdowns
  useEffect(() => {
    const debouncedFetchWarehouses = debounce(fetchWarehouses, 500);
    debouncedFetchWarehouses();
    return () => debouncedFetchWarehouses.cancel();
  }, [fetchWarehouses, warehousesCurrentPage]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle filter changes
  const handleFilterChange =
    (field: keyof BackOrderReportParams) =>
    (event: React.ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      setFilters({
        ...filters,
        [field]: event.target.value,
      });
      setCurrentPage(1);
    };

  // Handle search button click
  const handleSearch = () => {
    fetchData();
  };

  // Handle clear filters
  const handleClearFilters = () => {
     const defaultDates = getDefaultDates();
    setFilters({
      source_location: undefined,
      destination_location: undefined,
      part_code: undefined,
      part_name: "",
      to_no: undefined,
       start_date: defaultDates.startDate,
      end_date: defaultDates.endDate,
    });
    setSelectedSourceWarehouse(null);
    setSelectedDestinationWarehouse(null);
    setCurrentPage(1);
  };

  // Handle download
  const handleDownload = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        source_location: selectedSourceWarehouse?.id,
        destination_location: selectedDestinationWarehouse?.id,
      };
      delete params.page;
      delete params.page_size;
      await AllReportsApi.api.downloadBackOrderReport(params);
    } catch (err) {
      setError("Failed to download report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle pagination
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(Number(event.target.value));
    setCurrentPage(1);
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "PENDING":
        return { bgcolor: "#fff3e0", color: "#f57c00" };
      case "IN_TRANSIT":
        return { bgcolor: "#e3f2fd", color: "#1976d2" };
      case "DELIVERED":
        return { bgcolor: "#e8f5e9", color: "#2e7d32" };
      case "CANCELLED":
        return { bgcolor: "#ffebee", color: "#d32f2f" };
      default:
        return { bgcolor: "#f5f5f5", color: "#666" };
    }
  };

  // Calculate completion percentage
  const calculateCompletion = (received: number, ordered: number) => {
    if (ordered === 0) return 0;
    return Math.round((received / ordered) * 100);
  };

  return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent>
            <Typography variant="h4" sx={{ fontWeight: 600, color: "#000" }}>
              Back Order Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666", mt: 1 }}>
              Track transfer orders between warehouses and monitor pending
              deliveries
            </Typography>
          </CardContent>
        </Card>

        {/* Filters Section */}
        <Card sx={{ mb: 3, boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
              Filter Options
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(4, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}>
              <Autocomplete
                fullWidth
                size="small"
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={selectedSourceWarehouse}
                onChange={(event, newValue) => {
                  setSelectedSourceWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setWarehousesCurrentPage(0);
                  setWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Source Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleScroll,
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <Autocomplete
                fullWidth
                size="small"
                options={warehouses}
                getOptionLabel={(option) => option.name}
                value={selectedDestinationWarehouse}
                onChange={(event, newValue) => {
                  setSelectedDestinationWarehouse(newValue);
                }}
                onInputChange={(event, newInputValue) => {
                  setWarehousesCurrentPage(0);
                  setWarehousesSearchQuery(newInputValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Destination Warehouse"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                          borderColor: "#000",
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#000",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  onScroll: handleScroll,
                  style: { maxHeight: 200, overflow: "auto" },
                }}
              />
              <TextField
                label="Reference Code"
                value={filters.part_code || ""}
                onChange={handleFilterChange("part_code")}
                size="small"
                inputProps={{
                  inputMode: "text",
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Part Name"
                value={filters.part_name}
                onChange={handleFilterChange("part_name")}
                size="small"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Transfer Order No"
                value={filters.to_no || ""}
                onChange={handleNumericInputChange("to_no")}
                size="small"
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="Start Date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange("start_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
              <TextField
                label="End Date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange("end_date")}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <CalendarIcon sx={{ mr: 1, color: "#666" }} />
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused fieldset": {
                      borderColor: "#000",
                    },
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: "#000",
                  },
                }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                sx={{
                  borderColor: "#000",
                  color: "#000",
                  "&:hover": {
                    borderColor: "#000",
                    bgcolor: "#f5f5f5",
                  },
                }}>
                Clear Filters
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}>
                Search
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid #000",
                  "&:hover": {
                    bgcolor: "#f5f5f5",
                  },
                  "&:disabled": {
                    bgcolor: "#e0e0e0",
                    color: "#999",
                  },
                }}>
                Download Excel
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3, border: "1px solid #ff5252" }}
            onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Data Table */}
        <Card sx={{ boxShadow: 0, border: "1px solid #e0e0e0" }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer>
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                  <CircularProgress sx={{ color: "#000" }} />
                </Box>
              ) : (
                <>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                        <TableCell sx={{ fontWeight: 600 }}>TO No</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Source TO No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Source / Destination Warehouse
                        </TableCell>
                        {/* <TableCell sx={{ fontWeight: 600 }}>
                          Desination Warehouse
                        </TableCell> */}
                        <TableCell sx={{ fontWeight: 600 }}>
                          Reference Code{" "}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Part Name
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Quantity Status
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600 }}>
                          Completion
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Order Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Original Order Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          Received Date
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                            <Typography color="text.secondary">
                              No records found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        data.map((row, index) => {
                          const completion = calculateCompletion(
                            row.quantity_received,
                            row.quantity_ordered
                          );
                          return (
                            <TableRow
                              key={`${row.TO_no}-${index}`}
                              sx={{
                                "&:hover": { bgcolor: "#fafafa" },
                                "&:last-child td": { border: 0 },
                              }}>
                              <TableCell>{row.TO_no}</TableCell>
                              <TableCell>{row.source_TO_no}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}>
                                  <Chip
                                    label={row.source_warehouse_name}
                                    size="small"
                                    sx={{
                                      bgcolor: "#f5f5f5",
                                      border: "1px solid #e0e0e0",
                                    }}
                                  />
                                  <TransferIcon sx={{ color: "#666" }} />
                                  <Chip
                                    label={row.destination_warehouse_name}
                                    size="small"
                                    sx={{
                                      bgcolor: "#000",
                                      color: "#fff",
                                    }}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell>{row.reference_code}</TableCell>
                              <TableCell>
                                <Tooltip title={row.part_name}>
                                  <Typography
                                    sx={{
                                      maxWidth: 200,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}>
                                    {row.part_name}
                                  </Typography>
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}>
                                    {row.quantity_received} /{" "}
                                    {row.quantity_ordered}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color={
                                      row.quantity_pending > 0
                                        ? "error"
                                        : "success.main"
                                    }>
                                    {row.quantity_pending > 0
                                      ? `${row.quantity_pending} pending`
                                      : "Complete"}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box
                                  sx={{
                                    position: "relative",
                                    display: "inline-flex",
                                  }}>
                                  <CircularProgress
                                    variant="determinate"
                                    value={completion}
                                    size={40}
                                    thickness={4}
                                    sx={{
                                      color:
                                        completion === 100
                                          ? "#4caf50"
                                          : completion >= 50
                                          ? "#ff9800"
                                          : "#f44336",
                                    }}
                                  />
                                  <Box
                                    sx={{
                                      top: 0,
                                      left: 0,
                                      bottom: 0,
                                      right: 0,
                                      position: "absolute",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}>
                                    <Typography
                                      variant="caption"
                                      component="div"
                                      color="text.secondary"
                                      sx={{ fontWeight: 600 }}>
                                      {completion}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              {/* <TableCell>
                                <Chip
                                  label={row.}
                                  size="small"
                                  sx={getStatusColor(row.status)}
                                />
                              </TableCell> */}
                              <TableCell>{row.order_date}</TableCell>
                              <TableCell>{row.original_order_date}</TableCell>
                              <TableCell>{row.received_date}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      p: 2,
                      borderTop: "1px solid #e0e0e0",
                      bgcolor: "#fafafa",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography variant="body2">Rows per page:</Typography>
                      <Select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(e as any)}
                        size="small"
                        sx={{
                          minWidth: 80,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e0e0e0",
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                          },
                        }}>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={200}>200</MenuItem>
                      </Select>
                    </Box>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      showFirstButton
                      showLastButton
                      sx={{
                        "& .MuiPaginationItem-root": {
                          "&.Mui-selected": {
                            bgcolor: "#000",
                            color: "#fff",
                            "&:hover": {
                              bgcolor: "#333",
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BackOrderReport;
